import axios from "axios";
import Cookies from "js-cookie";
import { getRefreshToken, authLogout } from "../store/actions/actions";

export const baseURL = process.env.REACT_APP_BASE_URL;
const api = axios.create({
  baseURL: baseURL,
  timeout: 30000,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      config.headers.Authorization = `Bearer ${token}`;
      resolve(api(config));
    }
  });
  failedQueue = [];
};

export const setupInterceptors = (dispatch) => {
  api.interceptors.request.use((config) => {
    const accessToken = Cookies.get("access_token");
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response &&
        error.response.status === 401 &&
        error.response.data?.code === "token_not_valid" &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        if (isRefreshing) {
          // Queue this request until refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject, config: originalRequest });
          });
        }

        isRefreshing = true;

        try {
          const refreshToken = Cookies.get("refresh_token");
          if (!refreshToken) {
            dispatch(authLogout());
            return Promise.reject(error);
          }

          const response = await getRefreshToken(refreshToken);

          if (response) {
            Cookies.set("access_token", response.access, { secure: true, sameSite: "strict" });
            Cookies.set("refresh_token", response.refresh, { secure: true, sameSite: "strict" });

            // Retry all queued requests with new token
            processQueue(null, response.access);

            // Retry the original request
            originalRequest.headers.Authorization = `Bearer ${response.access}`;
            return api(originalRequest);
          } else {
            processQueue(error);
            dispatch(authLogout());
            return Promise.reject(error);
          }
        } catch (refreshError) {
          console.error("Error refreshing access token:", refreshError);
          processQueue(refreshError);
          dispatch(authLogout());
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

export default api;
